import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { JsonValue } from '@backstage/types';
import { useApi } from '@backstage/frontend-plugin-api';
import { AnalyticsContext } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder';
import {
  useCustomFieldExtensions,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { Workflow } from '@backstage/plugin-scaffolder-react/alpha';
import { scaffolderFieldExtensionsElement } from './fieldExtensions';

/**
 * The template form, reached from a card's Choose button.
 *
 * The scaffolder's stock Templates sub-page is a two-route switch — the list at
 * the index, this form at `:namespace/:templateName`. Replacing that sub-page
 * with a list alone (which is what we do, to restyle the list) drops the second
 * route, and Choose lands on a URL that renders the list again. So the route is
 * restated here.
 *
 * The plugin's own TemplateWizardPageContent is internal, so this rebuilds it
 * from public API. It is deliberately the same shape: resolve the template ref
 * from the path, hand the manifest to Workflow, and on submit call
 * scaffolderApi.scaffold and follow the task. Two things the stock content has
 * are not reproduced, neither of which is wired up in this app:
 *
 *  - form decorators (FormDecoratorBlueprint) — we register none
 *  - form fields contributed through formFieldsApi — the scaffolder's own are
 *    the same pickers supplied by ./fieldExtensions, and we add no others
 *
 * Add either of those later and this component has to grow to match.
 */
export const TemplateWizard = () => {
  const { namespace = 'default', templateName = '' } = useParams();
  const scaffolderApi = useApi(scaffolderApiRef);
  const { secrets } = useTemplateSecrets();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const fieldExtensions = useCustomFieldExtensions(
    scaffolderFieldExtensionsElement,
  );

  const templateRef = stringifyEntityRef({
    kind: 'Template',
    namespace,
    name: templateName,
  });

  const onCreate = useCallback(
    async (values: Record<string, JsonValue>) => {
      // Guard against a double submit landing two tasks on one click.
      if (creating) {
        return;
      }
      setCreating(true);
      try {
        const { taskId } = await scaffolderApi.scaffold({
          templateRef,
          values,
          secrets,
        });
        navigate(`/create/request/task/${taskId}`);
      } catch (e) {
        setCreating(false);
        throw e;
      }
    },
    [creating, navigate, scaffolderApi, secrets, templateRef],
  );

  // A bad template name in the URL is a dead end; Workflow surfaces the error
  // and we drop back to the list, as the stock wizard does.
  const onError = useCallback(() => <Navigate to="/create/templates" />, []);

  return (
    <AnalyticsContext attributes={{ entityRef: templateRef }}>
      {creating && <Progress />}
      {/* Capped like the request page: at 1400px a text box is a line across
          the screen, and the stepper's labels drift too far apart to read as
          one sequence. */}
      <div style={{ maxWidth: 1040, margin: '0 auto' }}>
        <Workflow
          namespace={namespace}
          templateName={templateName}
          onCreate={onCreate}
          onError={onError}
          extensions={fieldExtensions}
          // Render the form with Backstage UI instead of @rjsf/material-ui.
          //
          // This is the single change that modernises every STOCK control in
          // every template — the selects on the environment path, the radios, the
          // checkboxes, the array add/remove buttons, the error summary. They
          // were MUI v4, which is where the wizard's dated feel came from; the
          // rest of this app (catalog pages, entity pages) has been Backstage UI
          // for a while, so the create flow was the odd one out.
          //
          // It also obliges every CUSTOM field to be Backstage UI. The theme is
          // published through ScaffolderThemeProvider, and a MUI field inside a
          // BUI form is a visibly different input sitting next to the stock ones
          // — worse than the uniform old look it replaced. All of ours were
          // rewritten in the same change for that reason.
          //
          // `EXPERIMENTAL_` is Backstage's marker, not a warning about
          // readiness: the BUI theme ships complete in 1.53 (20 widgets, 17 RJSF
          // templates). The prefix means the PROP may be renamed, which is a
          // one-line follow-up on upgrade, not a re-do.
          formProps={{ EXPERIMENTAL_theme: 'bui' }}
        />
      </div>
    </AnalyticsContext>
  );
};
